/**
 * Run Workflow Modal
 *
 * Modal component for executing a workflow with optional JSON input.
 */
import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { JsonEditor } from '../ui/json-editor';
import { useRunWorkflow } from '../../hooks/useWorkflows';

import { SchemaForm } from '@/components/schema-form/SchemaForm';
import { useAgent } from '@/hooks';
import { useSchedules } from '@/hooks/useSchedules';
import type { Schedule } from '@/services/schedules';
import { useNavigate } from 'react-router-dom';

import { useWorkflow, useWorkflowExecution } from '@/hooks/useWorkflows';
import type { WorkflowStep } from '@/types/workflow';
import { initialValue, isRenderableSchema, pruneEmpty } from '@/types/inputSchema';

interface RunWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  workflowId: string;
  workflowName: string;
}

type ModalView = 'input' | 'running' | 'result';

export function RunWorkflowModal({
  isOpen,
  onClose,
  workflowId,
  workflowName,
}: RunWorkflowModalProps) {
  // A workflow hands its input to the first step, so that step's agent is what
  // declares the shape. This modal used to guess it from keywords in the
  // workflow NAME and pre-fill a customer-support ticket for anything it did
  // not recognise — then re-apply that on every open, wiping what you typed.
  const { data: workflow } = useWorkflow(isOpen ? workflowId : undefined);
  const firstAgentId = workflow?.steps
    ?.slice()
    .sort((a: WorkflowStep, b: WorkflowStep) => a.order - b.order)
    .find((step: WorkflowStep) => (step.type ?? 'agent') === 'agent')?.agentId;
  const { data: firstAgent } = useAgent(isOpen ? firstAgentId : undefined);
  const schema = firstAgent?.inputSchema;
  const hasForm = isRenderableSchema(schema);

  // If this workflow runs on a schedule, that schedule's input is what actually
  // executes every morning. Start from it, so pressing Run tests the real thing
  // rather than an empty form that happens to look the same.
  const { data: schedules } = useSchedules();
  const scheduleInput = schedules?.find((sch: Schedule) => sch.workflowId === workflowId)?.inputData;

  const [view, setView] = useState<ModalView>('input');
  const [inputJson, setInputJson] = useState('');
  const [formValue, setFormValue] = useState<Record<string, unknown>>({});
  const [useJsonMode, setUseJsonMode] = useState(false);
  const [isJsonValid, setIsJsonValid] = useState(true);
  const [runId, setRunId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const runWorkflow = useRunWorkflow();

  // Follow the run we just started. The modal used to stop at "Workflow
  // Started!" and tell the user to go and look somewhere else, which is the one
  // thing they were already doing by pressing Run.
  const { data: execution } = useWorkflowExecution(
    runId ? workflowId : undefined,
    runId ?? undefined
  );

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setView('input');
      setIsJsonValid(true);
      setRunId(null);
      setError(null);
    }
  }, [isOpen]);

  // Seed from the schema once it loads, keyed on the schema so it does not
  // overwrite what the user has already typed.
  useEffect(() => {
    if (isOpen && isRenderableSchema(schema)) {
      setFormValue(initialValue(schema, scheduleInput ?? null));
    }
  }, [isOpen, schema, scheduleInput]);

  const handleValidationChange = useCallback((valid: boolean) => {
    setIsJsonValid(valid);
  }, []);

  const showingForm = hasForm && !useJsonMode;

  const handleRun = async () => {
    if (!showingForm && !isJsonValid && inputJson.trim()) {
      setError('Please fix JSON errors before running');
      return;
    }

    setView('running');
    setError(null);

    try {
      const inputData = showingForm
        ? (pruneEmpty(formValue) as Record<string, unknown> | undefined)
        : inputJson.trim()
          ? JSON.parse(inputJson)
          : undefined;

      runWorkflow.mutate(
        { workflowId, data: inputData ? { input: inputData } : undefined },
        {
          onSuccess: (result) => {
            setRunId(result.runId);
            setView('result');
          },
          onError: (err) => {
            setError(err.message || 'Failed to run workflow');
            setView('input');
          },
        }
      );
    } catch (err) {
      setError('Invalid JSON input');
      setView('input');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Run Workflow</h2>
            <p className="text-sm text-muted-foreground">{workflowName}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            ✕
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {view === 'input' && (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor={showingForm ? undefined : 'input'}>
                    {showingForm ? 'Input' : 'Input Data (JSON)'}
                  </Label>
                  {hasForm && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto py-1 text-xs"
                      onClick={() => {
                        if (!useJsonMode) {
                          setInputJson(JSON.stringify(pruneEmpty(formValue) ?? {}, null, 2));
                        }
                        setUseJsonMode((m) => !m);
                      }}
                    >
                      {useJsonMode ? 'Use the form' : 'Edit as JSON'}
                    </Button>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  Passed to the workflow&apos;s first step.
                </p>
                {showingForm ? (
                  <SchemaForm schema={schema!} value={formValue} onChange={setFormValue} />
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <JsonEditor
                      value={inputJson}
                      onChange={setInputJson}
                      onValidationChange={(valid) => handleValidationChange(valid)}
                      height="200px"
                    />
                  </div>
                )}
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

            </div>
          )}

          {view === 'running' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
              <p className="text-lg font-medium">Running workflow...</p>
              <p className="text-sm text-muted-foreground">
                This may take a few moments
              </p>
            </div>
          )}

          {view === 'result' && (
            <div className="space-y-4">
              {(() => {
                const status = execution?.status ?? 'RUNNING';
                const done = status === 'COMPLETED';
                const failed = status === 'FAILED' || status === 'CANCELLED';
                const tone = failed
                  ? 'bg-red-50 border-red-200 text-red-800'
                  : done
                    ? 'bg-green-50 border-green-200 text-green-800'
                    : 'bg-blue-50 border-blue-200 text-blue-800';
                return (
                  <div className={`flex items-center gap-3 rounded-lg border p-4 ${tone}`}>
                    <span className="text-2xl">{failed ? '⚠️' : done ? '✅' : '⏳'}</span>
                    <div>
                      <p className="font-medium">
                        {failed ? 'Run failed' : done ? 'Run finished' : 'Running…'}
                      </p>
                      <p className="text-sm opacity-80">
                        {execution?.errorMessage ??
                          (done
                            ? 'The result is on the execution page.'
                            : 'This updates on its own — you can close the dialog.')}
                      </p>
                    </div>
                  </div>
                );
              })()}

              {execution && (
                <dl className="grid grid-cols-3 gap-3 rounded-lg border p-4 text-sm">
                  <div>
                    <dt className="text-muted-foreground">Status</dt>
                    <dd className="font-medium">{execution.status}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Tokens</dt>
                    <dd className="font-medium tabular-nums">
                      {execution.totalTokensUsed?.toLocaleString() ?? '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Cost</dt>
                    <dd className="font-medium tabular-nums">
                      {execution.totalCostCents != null
                        ? `${execution.totalCostCents.toFixed(2)}¢`
                        : '—'}
                    </dd>
                  </div>
                </dl>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end gap-2">
          {view === 'input' && (
            <>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handleRun}
                disabled={!isJsonValid && inputJson.trim() !== ''}
              >
                🚀 Run Workflow
              </Button>
            </>
          )}
          {view === 'running' && (
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          )}
          {view === 'result' && (
            <>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button variant="outline" onClick={() => setView('input')}>
                Run again
              </Button>
              {runId && (
                <Button
                  onClick={() => {
                    onClose();
                    navigate(`/workflows/${workflowId}/executions/${runId}`);
                  }}
                >
                  Open this run
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default RunWorkflowModal;
